// BlackOS (WS-F): admin editing of library listings.
// PATCH /provider-listings/:id edits name, description, readme, logo, categories,
// collections, rank and visibility; POST /provider-listings/:id/reset drops the
// customization. Exported through ./index.ts.
import type { DashboardInstanceProviderListingsUpdateBody } from '@metorial/dashboard-sdk';
import { createLoader } from '@metorial/data-hooks';
import { withAuth } from '../user';
import { providerListingLoader, providerListingsLoader } from './loaders/providerListing';

export let providerListingAdminLoader = createLoader({
  name: 'providerListingAdmin',
  // Refetch the provider page header and the catalog grid after an edit.
  parents: [providerListingLoader, providerListingsLoader],
  fetch: (i: { instanceId: string; providerListingId: string }) =>
    withAuth(sdk => sdk.providers.listings.get(i.instanceId, i.providerListingId)),
  mutators: {
    update: (
      body: DashboardInstanceProviderListingsUpdateBody,
      { input }: { input: { instanceId: string; providerListingId: string } }
    ) =>
      withAuth(sdk =>
        sdk.providers.listings.update(input.instanceId, input.providerListingId, body)
      ),
    reset: (
      _: {},
      { input }: { input: { instanceId: string; providerListingId: string } }
    ) =>
      withAuth(sdk => sdk.providers.listings.reset(input.instanceId, input.providerListingId))
  }
});

export let useProviderListingAdmin = (
  instanceId: string | null | undefined,
  providerListingId: string | null | undefined
) => {
  let data = providerListingAdminLoader.use(
    instanceId && providerListingId ? { instanceId, providerListingId } : null
  );

  return {
    ...data,
    useUpdateMutator: data.useMutator('update'),
    useResetMutator: data.useMutator('reset')
  };
};

export let providerCollectionsLoader = createLoader({
  name: 'providerCollections',
  parents: [],
  fetch: (i: { instanceId: string }) =>
    withAuth(sdk => sdk.providers.collections.list(i.instanceId, { limit: 100 })),
  mutators: {}
});

export let useProviderCollections = (instanceId: string | null | undefined) =>
  providerCollectionsLoader.use(instanceId ? { instanceId } : null);

// All categories (the default page size would cut the list short).
export let providerCategoriesAdminLoader = createLoader({
  name: 'providerCategoriesAdmin',
  parents: [],
  fetch: (i: { instanceId: string }) =>
    withAuth(sdk => sdk.providers.categories.list(i.instanceId, { limit: 100 })),
  mutators: {}
});

export let useAllProviderCategories = (instanceId: string | null | undefined) =>
  providerCategoriesAdminLoader.use(instanceId ? { instanceId } : null);
